import { useMemo, useState } from 'react';
import { usePublishersStore } from '../../store/models';
import { useErrorStore } from '../../store/models/error';
import useTriggeredErrorDialog from '../../store/models/error/useTriggeredErrorDialog';
import { serviceErrorSerializer } from '../../store/models/publishers/errors/serviceErrors';
import useStoreModuleDependency from '../hooks/useStoreModuleDependency';
import useEcalWorldAsDev from './useEcalWorldAsDev';

function useEcalWorldProfile (props) {
    const { publisher } = props;
    const publishersStore = usePublishersStore();
    const errorStore = useErrorStore();
    const { devAccess } = useEcalWorldAsDev();

    const [confirmOpen, setConfirmOpen] = useState(false);

    // Guards against reacting to an updatePublisherDetails failure triggered by
    // useCoverPhoto's own upload/delete calls on the same page.
    const { renderErrorDialog, markTriggered, clearTriggered } = useTriggeredErrorDialog({
        hook: 'useEcalWorldProfile',
        serviceErrorSerializer,
        errorObservables: [
            errorStore.observed.errors.getPublisherDetails,
            errorStore.observed.errors.updatePublisherDetails,
        ],
    });

    // Reload via the restricted endpoint so publicAccess/coverPhoto both reflect
    // what's actually saved, rather than relying on the login-time getPublisherDetails
    // fetch (which doesn't select coverPhoto).
    useStoreModuleDependency({
        initStore: async () => {
            markTriggered();
            await publishersStore.getPublisherDetailsRestricted();
            clearTriggered();
        },
    });

    // publisherDetails is fetched keyed to whichever publisher's token was last used
    // (see authGetPublisherInfo), so once it's loaded it's authoritative for this publisher.
    const isPublic = useMemo(() => {
        const publisherDetails = publishersStore.publisherDetails;
        if (publisherDetails) {
            return publisherDetails.publicAccess === true;
        }
        return publisher.publicAccess === true;
    }, [publishersStore.observed.publisherDetails, publisher.publicAccess]);

    const tooltipTitle = isPublic
        ? 'Your organisation is discoverable in ECAL World — click to disable'
        : 'Make your organisation discoverable in ECAL World';

    const dialogTitle = isPublic ? 'Disable ECAL World Discoverability' : 'Enable ECAL World Discoverability';

    const dialogText = isPublic
        ? 'Are you sure you want to make your organisation undiscoverable in ECAL World?'
        : 'Are you sure you want to make your organisation discoverable in ECAL World?';

    function handleToggle (e) {
        e.stopPropagation();
        e.preventDefault();
        setConfirmOpen(true);
    }

    function handleIntercomTrace () {
         if (typeof window.Intercom === 'function') {
            window.Intercom('update', {
                user_id: publisher?.publisherId,
                email: publisher?.email,
                user_hash: publisher?.intercomUserHash,
                ecal_world_enabled: !isPublic,
            });
        }
    }

    async function handleConfirm () {
        // Close immediately rather than waiting on the request — the global
        // progress indicator (shown by markTriggered/hidden by clearTriggered)
        // takes over as the "in flight" signal, and closes on either success
        // or failure (failure via useModuleErrorDialog's own effect).
        setConfirmOpen(false);
        handleIntercomTrace()
        markTriggered();
        await publishersStore.updatePublisherDetailsRestricted({ publicAccess: !isPublic });
        clearTriggered();
    }

    function handleCancel () {
        setConfirmOpen(false);
    }

    return {
        devAccess,
        isPublic,
        confirmOpen,
        tooltipTitle,
        dialogTitle,
        dialogText,
        handleToggle,
        handleConfirm,
        handleCancel,
        renderErrorDialog,
    };
}

export default useEcalWorldProfile;
